import type { PublicClientApplication } from '@azure/msal-browser'

// import { API_SCOPE } from '@/utils/auth/config'
import { TOKEN_API_CLIENT_ID, TOKEN_API_CLIENT_SECRET, TOKEN_API_URL } from '../env'

const API_TOKEN_KEY = 'KONG_API_TOKEN'

export async function getCurrentToken(msalInstance: PublicClientApplication): Promise<string | null> {
  const acquireAccessToken = async () => {
    const activeAccount = msalInstance.getActiveAccount() // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
    const accounts = msalInstance.getAllAccounts()

    if (!activeAccount && accounts.length === 0) {
      /*
       * User is not signed in. Throw error or wait for user to login.
       * Do not attempt to log a user in outside of the context of MsalProvider
       */
      return null
    }

    const request = {
      scopes: [],
      account: activeAccount || accounts[0]
    }

    try {
      const authResult = await msalInstance.acquireTokenSilent(request)

      return authResult.accessToken
    } catch (error) {
      // If silent acquisition fails, try acquiring token through popup or redirect
      try {
        const authResult = await msalInstance.acquireTokenPopup(request)

        return authResult.accessToken
      } catch (error) {
        console.error('Error acquiring token:', error)

        return null
      }
    }
  }

  let token = null

  if (typeof window !== 'undefined') {
    token = await acquireAccessToken()
  }

  return token
}

type GetAPITokenResponse = { token_type: string; access_token: string; expires_in: number }
let tokenPromise: Promise<string | null> | null = null

let cachedToken: { access_token: string; expiry_time: number } | null = null

try {
  localStorage.removeItem(API_TOKEN_KEY)
} catch {}

export const getKONGToken = async () => {
  if (cachedToken && Date.now() < cachedToken.expiry_time) {
    return cachedToken.access_token
  }

  if (tokenPromise) return tokenPromise

  tokenPromise = new Promise<string | null>(async resolve => {
    try {
      const formData = new URLSearchParams()

      formData.append('grant_type', 'client_credentials')
      formData.append('client_id', TOKEN_API_CLIENT_ID!)
      formData.append('client_secret', TOKEN_API_CLIENT_SECRET!)

      const tokenRes = await fetch(TOKEN_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData
      })

      if (tokenRes.ok) {
        const parsedToken = (await tokenRes.json()) as GetAPITokenResponse

        cachedToken = {
          access_token: parsedToken.access_token,
          expiry_time: Date.now() + parsedToken.expires_in * 0.8 * 1000
        }
        resolve(parsedToken.access_token)
      } else {
        console.error('Failed to fetch token:', tokenRes.status, await tokenRes.text()) // Log the error
        resolve(null) // Resolve with null on error
      }
    } catch (error) {
      console.error('Error fetching token:', error) // Log the error
      resolve(null) // Resolve with null on error
      throw new Error('Failed to fetch KONG token')
    } finally {
      tokenPromise = null // Reset the promise after completion (success or failure)
    }
  })

  return tokenPromise
}
